import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingContext = createContext();

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
};

export const BookingProvider = ({ children }) => {
  const { user } = useAuth();
  const [myBookingsCount, setMyBookingsCount] = useState({
    pending: 0,
    statusChanged: 0, // confirmed or cancelled
    total: 0
  });
  const [bookingRequestsCount, setBookingRequestsCount] = useState({
    pending: 0,
    statusChanged: 0,
    total: 0
  });

  // Check for recent booking changes and show notifications
  const checkRecentChanges = useCallback(async () => {
    if (!user) return;
    
    console.log('📬 [BookingContext] Checking recent changes for user:', user.id);
    
    try {
      const token = localStorage.getItem('skillsos_token');
      if (!token) {
        console.log('📬 [BookingContext] No token found');
        return;
      }
      
      console.log('📬 [BookingContext] Calling /bookings/recent-changes');
      const response = await axios.get(`${API}/bookings/recent-changes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { changes, count } = response.data;
      console.log('📬 [BookingContext] Received changes:', { changes, count });
      
      if (count > 0) {
        // NOTE: Toast notifications disabled - user will see:
        // 1. Red badge number on Notifications button
        // 2. Notification in dropdown menu
        // This is less intrusive than pop-up toasts
        console.log('📬 [BookingContext] Found', count, 'recent changes (not showing toast)');
        
        // Mark notifications as seen after user has had time to see them
        setTimeout(async () => {
          try {
            await axios.post(
              `${API}/users/mark-notifications-seen`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error('Error marking notifications as seen:', err);
          }
        }, 5000); // Wait 5 seconds before marking as seen
      } else {
        console.log('📬 [BookingContext] No recent changes found');
      }
    } catch (error) {
      console.error('📊 [BookingContext] Error checking recent changes:', error);
    }
  }, [user]);

  // Fetch booking counts for the current user
  const fetchBookingCounts = async () => {
    if (!user) {
      setMyBookingsCount({ pending: 0, statusChanged: 0, total: 0 });
      setBookingRequestsCount({ pending: 0, statusChanged: 0, total: 0 });
      return;
    }
    
    try {
      const token = localStorage.getItem('skillsos_token');
      console.log('📊 [BookingContext] Token check:', token ? `Found (${token.length} chars)` : 'NOT FOUND');
      
      if (!token) {
        console.log('📊 [BookingContext] No token - skipping fetch');
        setMyBookingsCount({ pending: 0, statusChanged: 0, total: 0 });
        setBookingRequestsCount({ pending: 0, statusChanged: 0, total: 0 });
        return;
      }
      
      console.log('📊 [BookingContext] Fetching counts with token...');
      const response = await axios.get(`${API}/bookings/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      console.log('📊 [BookingContext] Received counts from backend:', data);
      
      setMyBookingsCount({
        pending: data.my_bookings.pending,
        statusChanged: data.my_bookings.status_changed,
        total: data.my_bookings.total
      });
      
      setBookingRequestsCount({
        pending: data.booking_requests.pending,
        statusChanged: data.booking_requests.status_changed || 0,
        total: data.booking_requests.total
      });
      
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('📊 [BookingContext] Not authenticated');
      } else {
        console.error('📊 [BookingContext] Error fetching booking counts:', error);
      }
    }
  };

  // Initial load and polling
  useEffect(() => {
    if (user && localStorage.getItem('skillsos_token')) {
      // Check for recent changes on first load (shows notifications)
      checkRecentChanges();
      
      // Fetch booking counts
      fetchBookingCounts();
      
      // Poll for booking updates every 10 seconds for near real-time notifications
      const interval = setInterval(() => {
        if (localStorage.getItem('skillsos_token')) {
          fetchBookingCounts();
        }
      }, 10000);
      
      return () => clearInterval(interval);
    } else {
      // Clear data when user logs out
      setMyBookingsCount({ pending: 0, statusChanged: 0, total: 0 });
      setBookingRequestsCount({ pending: 0, statusChanged: 0, total: 0 });
    }
  }, [user, checkRecentChanges]);

  // Clear my bookings notification badge (only status changes, not pending)
  const clearMyBookingsBadge = useCallback(async () => {
    console.log('📊 [BookingContext] Clearing My Bookings status change badge (keeping pending)');
    
    // Save timestamp to backend
    try {
      const token = localStorage.getItem('skillsos_token');
      if (token) {
        await axios.post(
          `${API}/users/update-last-viewed`,
          { page: 'my_bookings' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('📊 [BookingContext] Error saving last viewed timestamp:', error);
    }
    
    // Also save to localStorage as backup
    localStorage.setItem('lastViewedMyBookings', new Date().toISOString());
    
    // Only clear statusChanged, NOT pending - pending bookings need attention (payment)!
    setMyBookingsCount(prev => ({
      ...prev,
      statusChanged: 0
      // pending is NOT cleared - it will update on next fetch based on actual pending bookings
    }));
  }, []);

  // Clear booking requests notification badge (only status changes, not pending)
  const clearBookingRequestsBadge = useCallback(async () => {
    console.log('📊 [BookingContext] Clearing Booking Requests status change badge (keeping pending)');
    
    // Save timestamp to backend
    try {
      const token = localStorage.getItem('skillsos_token');
      if (token) {
        await axios.post(
          `${API}/users/update-last-viewed`,
          { page: 'booking_requests' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('📊 [BookingContext] Error saving last viewed timestamp:', error);
    }
    
    // Also save to localStorage as backup
    localStorage.setItem('lastViewedBookingRequests', new Date().toISOString());
    
    // Only clear statusChanged, NOT pending - pending requests always need attention!
    setBookingRequestsCount(prev => ({
      ...prev,
      statusChanged: 0
      // pending is NOT cleared - it will update on next fetch based on actual pending bookings
    }));
  }, []);

  const value = {
    myBookingsCount,
    bookingRequestsCount,
    fetchBookingCounts,
    clearMyBookingsBadge,
    clearBookingRequestsBadge
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};
